import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Film, Lock, User as UserIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(username, password);
      toast.success("Signed in");
      navigate("/");
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 relative overflow-hidden">
      {/* Left cinematic panel */}
      <div className="hidden lg:flex flex-col justify-between p-14 relative border-r border-white/5">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1578662996442-48f60103fc96?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxjaW5lbWF0aWMlMjBmaWxtJTIwYWJzdHJhY3QlMjB0ZXh0dXJlJTIwZGFya3xlbnwwfHx8fDE3ODM5NDEwMDR8MA&ixlib=rb-4.1.0&q=85)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black/70 to-transparent" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-10 h-10 border border-primary/60 flex items-center justify-center neon-border">
            <Film className="w-5 h-5 text-primary" />
          </div>
          <div>
            <div className="text-xs tracking-[0.3em] text-primary font-mono">
              ANUTRACKS12
            </div>
            <div className="text-sm font-medium">Subtitle Studio</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="text-5xl xl:text-6xl font-black leading-[0.9] tracking-tighter">
            Every word.
            <br />
            <span className="neon-text">Every language.</span>
            <br />
            Perfectly timed.
          </h1>
          <p className="mt-6 text-muted-foreground max-w-md text-sm">
            Whisper-grade transcription, LLM-crafted translation across
            English, <span className="script-ml">മലയാളം</span>,{" "}
            <span className="script-ta">தமிழ்</span>,{" "}
            <span className="script-te">తెలుగు</span>, and{" "}
            <span className="script-hi">हिन्दी</span> — with broadcast-ready
            SRT export.
          </p>
          <div className="mt-8 flex gap-2 text-[10px] font-mono tracking-widest text-muted-foreground">
            <span className="px-2 py-1 border border-white/10">WHISPER-1</span>
            <span className="px-2 py-1 border border-white/10">GPT-5.2</span>
            <span className="px-2 py-1 border border-white/10">CLAUDE 4.6</span>
            <span className="px-2 py-1 border border-white/10">GEMINI 3</span>
          </div>
        </div>
      </div>

      {/* Right auth panel */}
      <div className="flex items-center justify-center p-6 sm:p-14 relative">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="text-xs tracking-[0.3em] text-primary font-mono mb-3">
              // AUTHORISED ACCESS
            </div>
            <h2 className="text-4xl sm:text-5xl font-black tracking-tighter">
              Sign in
            </h2>
            <p className="text-muted-foreground text-sm mt-2">
              Enter your credentials to open the studio.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-xs tracking-widest font-mono text-muted-foreground">
                USERNAME
              </Label>
              <div className="relative">
                <UserIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="username"
                  data-testid="login-username-input"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                  className="pl-10 h-12 bg-secondary/60 border-white/10 rounded-none"
                  placeholder="AnuTracks_Admin"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-widest font-mono text-muted-foreground">
                PASSWORD
              </Label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  data-testid="login-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                  className="pl-10 h-12 bg-secondary/60 border-white/10 rounded-none"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-destructive border border-destructive/40 bg-destructive/10 px-3 py-2"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-12 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-semibold tracking-wide"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signing in
                </>
              ) : (
                "Enter Studio →"
              )}
            </Button>

            <div className="pt-4 border-t border-white/5 text-[10px] font-mono tracking-widest text-muted-foreground">
              SECURED WITH JWT · BCRYPT · HTTP-ONLY COOKIE
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
